import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { PortalShell } from "@/components/portal/PortalShell";
import { STUDENT_PORTAL } from "@/components/portal/portalConfig";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import Onboarding from "@/pages/Onboarding";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import PYQBrowser from "@/pages/PYQBrowser";
import Session from "@/pages/Session";
import Stats from "@/pages/Stats";
import ChapterPractice from "@/pages/ChapterPractice";
import StudentDashboard from "@/pages/student/StudentDashboard";
import PracticeHub from "@/pages/student/PracticeHub";
import AIPractice from "@/pages/student/AIPractice";
import Settings from "@/pages/student/Settings";
import Bookmarks from "@/pages/student/Bookmarks";
import Mistakes from "@/pages/student/Mistakes";
import TopicPractice from "@/pages/student/TopicPractice";
import AdminPortal from "@/pages/admin/AdminPortal";
import TeacherPortal from "@/pages/teacher/TeacherPortal";

function StudentRoutes() {
  return (
    <ProtectedRoute roles={["STUDENT"]} requireOnboarding>
      <PortalShell config={STUDENT_PORTAL} />
    </ProtectedRoute>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* Public (marketing shell) */}
      <Route path="/" element={<Layout><Landing /></Layout>} />
      <Route path="/login" element={<Layout><Login /></Layout>} />
      <Route path="/register" element={<Layout><Register /></Layout>} />
      <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/onboarding" element={<Layout><ProtectedRoute><Onboarding /></ProtectedRoute></Layout>} />

      {/* Student portal (sidebar shell + nested routes) */}
      <Route path="/student" element={<StudentRoutes />}>
        <Route index element={<StudentDashboard />} />
        <Route path="practice" element={<PracticeHub />} />
        <Route path="practice/pyq" element={<PYQBrowser />} />
        <Route path="practice/ai" element={<AIPractice />} />
        <Route path="bookmarks" element={<Bookmarks />} />
        <Route path="mistakes" element={<Mistakes />} />
        <Route path="practice/topic" element={<TopicPractice />} />
        <Route path="session" element={<Session />} />
        <Route path="performance" element={<Stats />} />
        <Route path="weak-areas" element={<ChapterPractice />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Teacher & Admin (existing shells for now) */}
      <Route path="/teacher" element={<Layout><ProtectedRoute roles={["EXPERT"]}><TeacherPortal /></ProtectedRoute></Layout>} />
      <Route path="/admin" element={<Layout><ProtectedRoute roles={["ADMIN"]}><AdminPortal /></ProtectedRoute></Layout>} />

      {/* Legacy path redirects */}
      <Route path="/practice" element={<Navigate to="/student/practice/pyq" replace />} />
      <Route path="/session" element={<Navigate to="/student/session" replace />} />
      <Route path="/stats" element={<Navigate to="/student/performance" replace />} />
      <Route path="/chapters" element={<Navigate to="/student/weak-areas" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="bottom-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
